import { ActionIcon, Button, Menu, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconCopyPlus,
  IconPlayerPlayFilled,
  IconDotsVertical,
  IconStrikethrough,
  IconTrash,
  IconLogin2,
  IconPhoto,
  IconClock,
  IconEdit,
  IconLabelImportant,
  IconWorld,
  IconPlayerEject,
} from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { stepIdToPrevOpOrderAtom } from '~/atoms/atoms';
import { erroredOperationAtom } from '~/atoms/no-code-atoms';
import { useDeleteOperations } from '~/hooks/mutations/operations/useDeleteOperations';
import { useDuplicateOperation } from '~/hooks/mutations/operations/useDuplicateOperation';
import { useSkipOperations } from '~/hooks/mutations/operations/useSkipOperations';
import { isOperationOptimistic } from '~/hooks/mutations/optimistic-handles';
import { useUpdateStepDescription } from '~/hooks/mutations/steps/useUpdateStepDescription';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { useCallback } from 'react';
import { usePreviewFromLocation } from '~/hooks/vm/usePreviewFromLocation';
import { api } from '~/trpc-extra/api';
import { useUpdatePageAliases } from '~/hooks/mutations/operations/use-update-page-aliases';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { TimeoutOverrideFormModal } from './TimeoutOverrideFormModal';
import { handleMutationError } from '~/hooks/mutations/callback-handles';
import { handleMutationSuccess } from '~/hooks/mutations/callback-handles';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { useViewOperationImage } from './useViewOperationImage';

export const ICON_SIZE = 15;

const PAGE_ALIAS_FORM_SCHEMA = z.object({
  pageAlias: z.string().min(1, 'Current tab alias is required'),
  opensPageAlias: z
    .string()
    // if the user submits an empty opensPageAlias field, we reset that to null in the DB
    .transform((val) => (val === '' ? null : val))
    .nullable(),
});

const PageAliasFormModal = ({ operation, testContentId }: { operation: SelectedOperation; testContentId: string }) => {
  const { mutateAsync: updatePageAliases } = useUpdatePageAliases({ testContentId });

  const form = useForm({
    mode: 'controlled',
    validate: zod4Resolver(PAGE_ALIAS_FORM_SCHEMA),
    initialValues: {
      pageAlias: operation.pageAlias,
      opensPageAlias: operation.opensPageAlias,
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(async (values) => {
        await updatePageAliases({
          operationId: operation.id,
          pageAlias: values.pageAlias,
          opensPageAlias: values.opensPageAlias,
        });
        modals.closeAll();
      })}
      className="flex flex-col gap-2"
    >
      <TextInput
        className="mb-2"
        placeholder="Enter current tab alias"
        label={
          <div className="flex items-center gap-2">
            <IconWorld
              size={ICON_SIZE}
              className="text-blue-500"
            />
            Current Tab
          </div>
        }
        description="The current tab of the action."
        {...form.getInputProps('pageAlias')}
      />
      <TextInput
        className="mb-2"
        placeholder="Enter target tab alias"
        label={
          <div className="flex items-center gap-2">
            <IconLogin2
              size={ICON_SIZE}
              className="text-green-500"
            />
            Target Tab
          </div>
        }
        description="The tab that will be opened when this action is executed."
        {...form.getInputProps('opensPageAlias')}
      />
      <div className="flex justify-end gap-1">
        <Button
          onClick={modals.closeAll}
          className="mt-2"
          variant="default"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="mt-2"
          loading={form.submitting}
        >
          {`Save Tab${operation.opensPageAlias ? 's' : ''}`}
        </Button>
      </div>
    </form>
  );
};

export default function OperationControl({
  operation,
  testContentId,
  stepOrder,
  previewCursorAllowed,
  moveCursorEnabled = true,
  setEditing,
  parentContext,
  readonly,
}: {
  operation: SelectedOperation;
  testContentId: string;
  stepOrder: number;
  previewCursorAllowed: boolean;
  moveCursorEnabled?: boolean;
  setEditing?: (editing: boolean) => void;
  // parentContext: see callback-handles.ts comment
  parentContext?: OperationParentContext;
  readonly?: boolean;
}) {
  const trpcUtils = api.useUtils();
  const { data: testType } = api.testContent.get.useQuery(
    { id: testContentId },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!testContentId,
      select: (data) => data?.test.testType,
    },
  );

  const setStepIdToPrevOrderMap = useSetAtom(stepIdToPrevOpOrderAtom);
  const setErroredOperation = useSetAtom(erroredOperationAtom);

  const { recorderInitiator, updateLiveState } = useLiveStateManager();

  const { openOperationImage } = useViewOperationImage();

  const { mutateAsync: duplicateOperation } = useDuplicateOperation({ operation, testContentId, parentContext });
  const { mutateAsync: deleteOperations, isPending: isDeleting } = useDeleteOperations({
    testContentId,
    parentContext,
  });
  const { mutateAsync: skipOperations } = useSkipOperations({ testContentId, parentContext });
  const { mutateAsync: ejectComponent } = api.component.eject.useMutation({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId }),
    onError: (error) => handleMutationError({ trpcUtils, testContentId, message: error.message, context: undefined }),
  });

  const { updateStepDescription } = useUpdateStepDescription({ testContentId });

  const controlsDisabled = isOperationOptimistic(operation?.id ?? '') || isDeleting || readonly;

  const handleMoveCursorHere = useCallback(() => {
    updateLiveState((prev) => ({
      ...prev,
      recorderCursor: {
        stepId: operation.testStep?.id || '',
        stepOrder,
        operation: { id: operation.id, order: operation.order },
        parentContext,
      },
    }));
  }, [operation, stepOrder, updateLiveState, parentContext]);

  const handleToggleSkip = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      await skipOperations({ operationIds: [operation.id], skip: !operation.skip });
      // Components don't have a step ID
      if (operation.testStep?.id) {
        updateStepDescription({ stepId: operation.testStep.id }).catch(() => undefined);
      }
      setErroredOperation((cur) => (cur?.id === operation.id ? undefined : cur));
    },
    [operation, skipOperations, updateStepDescription, setErroredOperation],
  );

  const handleDelete = useCallback(() => {
    modals.openConfirmModal({
      title: 'Delete action',
      children: <Text>Are you sure you want to delete this action?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        try {
          await deleteOperations({ operationIds: [operation.id] });
          // Components don't have a step ID
          if (operation.testStep?.id) {
            updateStepDescription({ stepId: operation.testStep.id }).catch(() => undefined);
          }
          setErroredOperation(undefined);
        } catch (e) {
          console.error(e);
        }
      },
    });
  }, [operation, deleteOperations, updateStepDescription, setErroredOperation]);

  const previewFromLocation = usePreviewFromLocation({ testContentId });

  return (
    <>
      {/* TODO: disable editing if generation is running */}
      {operation?.id ?
        <Menu
          position="bottom-start"
          offset={-1}
          shadow="md"
        >
          <Menu.Target>
            <ActionIcon
              className="text-gray-700 dark:text-gray-200"
              variant="transparent"
              title="More options"
              onClick={(e) => e.stopPropagation()}
            >
              <IconDotsVertical size={ICON_SIZE} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
            {/* Disable preview to nested operations for now, but record still works */}
            {/* TODO: enable preview to nested operations 
              https://app.dartai.com/d/J0FQHEWT8Z0d/p9WrdwKyWKB7-Investigate-inability-to-previ */}
            {!operation.skip && !parentContext && (
              <>
                <Menu.Item
                  disabled={controlsDisabled || !previewCursorAllowed}
                  onClick={() =>
                    previewFromLocation({
                      step: { id: operation.testStep?.id || '', stepOrder },
                      operation: { id: operation.id, order: operation.order },
                    })
                  }
                  leftSection={
                    <IconPlayerPlayFilled
                      className="text-green-500"
                      size={ICON_SIZE}
                    />
                  }
                >
                  Preview to this action
                </Menu.Item>
                <Menu.Divider />
              </>
            )}

            {recorderInitiator?.executionType === 'record' && operation.testStep && moveCursorEnabled ?
              <Menu.Item
                title="Set newly recorded actions to be added after this action"
                disabled={controlsDisabled}
                onClick={handleMoveCursorHere}
                leftSection={
                  <div
                    className="flex items-center justify-center"
                    style={{ width: `${ICON_SIZE}px`, height: `${ICON_SIZE}px` }}
                  >
                    <div className="h-4 w-1 rounded-sm bg-red-500" />
                  </div>
                }
              >
                Move recorder cursor here
              </Menu.Item>
            : null}
            {!!setEditing && (
              <Menu.Item
                leftSection={<IconEdit size={ICON_SIZE} />}
                onClick={() => setEditing?.(true)}
              >
                Edit
              </Menu.Item>
            )}
            <Menu.Item
              disabled={controlsDisabled}
              onClick={handleToggleSkip}
              leftSection={<IconStrikethrough size={ICON_SIZE} />}
            >
              {operation.skip ? 'Unskip' : 'Skip'}
            </Menu.Item>
            {!parentContext?.componentId && (
              <>
                <Menu.Item
                  disabled={controlsDisabled}
                  onClick={() => duplicateOperation({ operationId: operation.id, skip: operation.skip })}
                  leftSection={<IconCopyPlus size={ICON_SIZE} />}
                >
                  Duplicate
                </Menu.Item>
                {testType !== 'MOBILE_APP' && (
                  <Menu.Item
                    disabled={controlsDisabled}
                    onClick={() => {
                      modals.open({
                        centered: true,
                        title: <div className="text-lg">{`Edit Action Tab${operation.opensPageAlias ? 's' : ''}`}</div>,
                        children: (
                          <PageAliasFormModal
                            operation={operation}
                            testContentId={testContentId}
                          />
                        ),
                      });
                    }}
                    leftSection={<IconLabelImportant size={ICON_SIZE} />}
                  >
                    Edit Tab
                  </Menu.Item>
                )}
                {(operation.type === 'ACTION' || operation.type === 'ASSERTION' || operation.type === 'NAVIGATION') && (
                  <Menu.Item
                    disabled={controlsDisabled}
                    onClick={() => {
                      modals.open({
                        centered: true,
                        title: (
                          <div className="text-lg">
                            Override{' '}
                            {operation.type === 'ACTION' ?
                              'Action'
                            : operation.type === 'ASSERTION' ?
                              'Assertion'
                            : 'Navigation'}{' '}
                            Timeout
                          </div>
                        ),
                        children: (
                          <TimeoutOverrideFormModal
                            operation={operation}
                            testContentId={testContentId}
                          />
                        ),
                      });
                    }}
                    leftSection={<IconClock size={ICON_SIZE} />}
                  >
                    Override{' '}
                    {operation.type === 'ACTION' ?
                      'Action'
                    : operation.type === 'ASSERTION' ?
                      'Assertion'
                    : 'Navigation'}{' '}
                    Timeout
                  </Menu.Item>
                )}
              </>
            )}
            {!!(operation.beforeScreenshot || operation.actionScreenshot || operation.afterScreenshot) && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconPhoto size={ICON_SIZE} />}
                  onClick={() => openOperationImage(operation.id)}
                >
                  View Screenshot
                </Menu.Item>
              </>
            )}
            <Menu.Divider />
            {operation.type === 'COMPONENT_CALL' && !parentContext?.componentId && (
              <Menu.Item
                leftSection={<IconPlayerEject size={ICON_SIZE} />}
                onClick={() =>
                  modals.openConfirmModal({
                    centered: true,
                    title: 'Eject from component',
                    children: (
                      <div>
                        <Text>This will extract all component actions into the current step.</Text>
                        <Text
                          size="sm"
                          c="dimmed"
                        >
                          This will not affect the main component.
                        </Text>
                      </div>
                    ),
                    labels: { confirm: 'Eject', cancel: 'Cancel' },
                    onConfirm: async () => {
                      await ejectComponent({
                        operationId: operation.id,
                      });
                    },
                  })
                }
              >
                Eject from component
              </Menu.Item>
            )}
            <Menu.Item
              disabled={controlsDisabled}
              leftSection={<IconTrash size={ICON_SIZE} />}
              onClick={handleDelete}
              color="red"
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      : <Button
          variant="light"
          onClick={() => setStepIdToPrevOrderMap(undefined)}
        >
          Cancel
        </Button>
      }
    </>
  );
}
